import { Box, Button, DialogActions, DialogContent, Grid, Link, Stack, Typography, useMediaQuery, useColorScheme } from '@mui/material';
import Lottie from 'lottie-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import { trackConnectFailed, trackConnectSelected, trackConnectSucceeded } from '../../../analytics/bpsEvents';
import { robotDataSlice } from '../../../redux/RobotDataSlice';
import { robotSlice } from '../../../redux/RobotSlice';
import { RobotActions, RobotArray, ROBOTS } from '../../../utils/constants';
import connectingDark from '../../Icons/Connection/bps-connecting-dark.json';
import connectingLight from '../../Icons/Connection/bps-connecting-light.json';
import connectionFailedDark from '../../Icons/Connection/bps-connection-failed-dark.json';
import connectionFailedLight from '../../Icons/Connection/bps-connection-failed-light.json';
import connectionSuccessDark from '../../Icons/Connection/bps-connected-dark.json';
import connectionSuccessLight from '../../Icons/Connection/bps-connected-light.json';

const ConnectionProgressionDialog = (props) => {
  const { onClose } = props;

  const { robotType, userRobotColor } = useSelector((state) => state.robotData);
  const { robotState, connectionType } = useSelector((state) => state.robot);

  const [dialogText, setDialogText] = useState();
  const [prevRobotName, setPrevRobotName] = useState(robotType.name);
  const [prevRobotObject, setPrevRobotObject] = useState(robotType);
  const [animationHeight, setAnimationHeight] = useState();
  // for devices like chromebooks that have wide but short screen ratios.
  const shortScreen = useMediaQuery(`(max-height: 700px)`);
  const { mode, systemMode } = useColorScheme();
  let colorMode = mode;
  if (mode === 'system') {
    colorMode = systemMode;
  }

  const [animation, setConnectionProgressionAnimation] = useState(colorMode === 'dark' ? connectingDark : connectingLight);

  const dispatch = useDispatch();

  const timerRef = useRef(null);

  useEffect(() => {
    if (shortScreen) {
      setAnimationHeight('109');
    } else {
      setAnimationHeight('256');
    }
  }, [shortScreen]);

  useEffect(() => {
    if (robotState === RobotActions.STATE.CONNECTED) {
      setConnectionProgressionAnimation(colorMode === 'dark' ? connectionSuccessDark : connectionSuccessLight);
      trackConnectSucceeded(connectionType, robotType.name);
      timerRef.current = setTimeout(() => {
        onClose();
      }, 2000);
    } else if (robotState === RobotActions.STATE.CONNECT_FAILED || robotState === RobotActions.STATE.CONNECT_FAILED_IN_CHARGER) {
      trackConnectFailed(connectionType, robotType.name);
      setConnectionProgressionAnimation(colorMode === 'dark' ? connectionFailedDark : connectionFailedLight);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [robotState, connectionType, colorMode, onClose, robotType.name]);

  useEffect(() => {
    /* If connection to the robot is lost during the connecting process.
     The robot type and robot name will be preseved in order to
     display dialogs correctly and allow for quicker reconnection */

    if (animation === connectingDark || animation === connectingLight || animation === connectionFailedDark || animation === connectionFailedLight) {
      if (robotType.name !== 'robotName') {
        setPrevRobotName(robotType.name);
      }
      if (robotType !== ROBOTS.NONE) {
        setPrevRobotObject(robotType);
      }
      setDialogText(<Trans i18nKey="connectionProgression.connecting">Connecting to {{ prevRobotName }}...</Trans>);
    } else if (animation === connectionSuccessDark || animation === connectionSuccessLight) {
      setDialogText(<Trans i18nKey="connectionProgression.connected">{{ prevRobotName }} Connected!</Trans>);
    }
    if (animation === connectionFailedDark || animation === connectionFailedLight) {
      setDialogText(<Trans i18nKey="connectionProgression.failed">Failed to Connect</Trans>);
    }
  }, [animation, robotType, prevRobotName]);

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick') {
      // Prevent closing on backdrop click
      return;
    }
    onClose(false);
  };

  const getSelectedRobot = () => {
    const selectedRobotType = prevRobotObject?.type;
    return RobotArray.find((selectedRobot) => selectedRobot.type === selectedRobotType);
  };

  const reconnectRobot = () => {
    const color = userRobotColor;
    trackConnectSelected(connectionType);
    batch(() => {
      dispatch(robotDataSlice.actions.onUpdateRobotType(getSelectedRobot()));
      dispatch(robotDataSlice.actions.onUpdateUserRobotColor(color));
      dispatch(robotSlice.actions.onUpdateRobotState(RobotActions.STATE.CONNECT));
    });

    handleClose();
  };

  return (
    <>
      <DialogContent>
        <Stack direction="column" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
          <Lottie
            animationData={animation}
            loop={animation === connectingLight || animation === connectingDark}
            style={{ height: `${animationHeight}px` }}
          />
          <Typography variant="h4" color="text.primary">
            {dialogText}
          </Typography>
          {(animation === connectionFailedDark || animation === connectionFailedLight) && (
            <Box>
              <Typography variant="body1" sx={{ marginBottom: '1rem' }} align="center" color="text.primary">
                {connectionType === RobotActions.STATE.CONNECTION_TYPE.SERIAL ? (
                  <Trans i18nKey="connectionProgression.connectionFailedInfoUsb">
                    Make sure your {{ prevRobotName }} is powered on and that the USB cable is securely connected to both the robot and your device.
                    Then try to connect again.
                  </Trans>
                ) : (
                  <Trans i18nKey="connectionProgression.connectionFailedInfo">
                    Make sure your {{ prevRobotName }} is powered on and close to your device. When you are ready, try to connect again.
                  </Trans>
                )}
              </Typography>
              <Grid container justifyContent="center">
                <Link href="https://support.sphero.com/en-US/connection-244332" target="_blank" rel="noopener noreferrer">
                  <Trans i18nKey="button.need_help">Need Help?</Trans>
                </Link>
              </Grid>
            </Box>
          )}
        </Stack>
      </DialogContent>
      {(animation === connectionFailedDark || animation === connectionFailedLight) && (
        <DialogActions style={{ justifyContent: 'center', margin: '2px 0' }}>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Button
              size="large"
              color="primary"
              variant="text"
              sx={{ fontSize: '1.35rem', padding: '.75rem 6rem', borderRadius: '20px' }}
              onClick={handleClose}
            >
              <Trans i18nKey="button.close">Close</Trans>
            </Button>
            <Button
              size="large"
              color="primary"
              variant="contained"
              sx={{ fontSize: '1.35rem', padding: '.75rem 6rem', borderRadius: '20px' }}
              onClick={reconnectRobot}
            >
              <Trans i18nKey="connectionProgression.retry">Retry</Trans>
            </Button>
          </Stack>
        </DialogActions>
      )}
    </>
  );
};

ConnectionProgressionDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default ConnectionProgressionDialog;
